import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Payments as PaymentsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  addDays,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import {
  getBranches,
  getEmployees,
  getPayrollSummary,
  exportPayrollCsv,
  getPayrollRuns,
  getPayrollRun,
  createPayrollRun,
  updatePayrollRun,
  deletePayrollRun,
  upsertPayrollPayment,
  deletePayrollPayment,
  exportPayrollRunCsv,
  getPayrollExpenses,
} from '../services/api';

const parseSalary = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Respaldo si el backend aún no tiene /api/payroll (reinicio pendiente). */
const buildSummaryFromEmployees = (employees, branchFilter, statusFilter) => {
  let list = employees;
  if (statusFilter && statusFilter !== 'all') {
    list = list.filter((e) => e.status === statusFilter);
  }
  if (branchFilter && branchFilter !== 'all') {
    list = list.filter((e) => {
      const bid = e.branch?._id || e.branch;
      return bid && String(bid) === String(branchFilter);
    });
  }

  const rows = list.map((emp) => ({
    _id: emp._id,
    firstName: emp.firstName,
    lastName: emp.lastName,
    position: emp.position,
    status: emp.status,
    employeeNumber: emp.employeeNumber,
    salary: parseSalary(emp.salary),
    branch: emp.branch
      ? { _id: emp.branch._id || emp.branch, name: emp.branch.name }
      : null,
  }));

  const byBranchMap = {};
  let grandTotal = 0;
  let withSalary = 0;
  let withoutSalary = 0;

  rows.forEach((row) => {
    const amount = row.salary ?? 0;
    if (row.salary == null) withoutSalary += 1;
    else {
      withSalary += 1;
      grandTotal += amount;
    }
    const branchKey = row.branch?._id?.toString() || 'sin-sucursal';
    if (!byBranchMap[branchKey]) {
      byBranchMap[branchKey] = {
        branch: row.branch || { _id: null, name: 'Sin sucursal' },
        employeeCount: 0,
        withSalaryCount: 0,
        withoutSalaryCount: 0,
        subtotal: 0,
        employees: [],
      };
    }
    const g = byBranchMap[branchKey];
    g.employeeCount += 1;
    if (row.salary == null) g.withoutSalaryCount += 1;
    else {
      g.withSalaryCount += 1;
      g.subtotal += amount;
    }
    g.employees.push(row);
  });

  return {
    filters: { branchId: branchFilter === 'all' ? null : branchFilter, status: statusFilter },
    totals: {
      employeeCount: rows.length,
      withSalaryCount: withSalary,
      withoutSalaryCount: withoutSalary,
      grandTotal,
    },
    byBranch: Object.values(byBranchMap).sort((a, b) =>
      (a.branch?.name || '').localeCompare(b.branch?.name || '')
    ),
    employees: rows,
  };
};

const getApiErrorMessage = (error) => {
  if (!error.response) {
    return 'No hay conexión con el servidor. Verifica que el backend esté en marcha (npm run dev) y reinicia el frontend.';
  }
  const status = error.response.status;
  const msg = error.response.data?.message;
  if (status === 403) {
    return msg || 'Solo el administrador puede ver la nómina. Inicia sesión con admin@empresa.com';
  }
  if (status === 401) {
    return msg || 'Sesión expirada. Cierra sesión e inicia de nuevo.';
  }
  if (status === 404) {
    return 'Ruta de nómina no encontrada. Reinicia el backend (npm run dev) para cargar el módulo nuevo.';
  }
  return msg || `Error del servidor (${status})`;
};

const formatMoney = (amount) => {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(amount);
};

const statusLabel = (status) => {
  const map = {
    active: 'Activo',
    inactive: 'Inactivo',
    on_leave: 'De permiso',
  };
  return map[status] || status;
};

const PERIOD_TYPES = [
  { value: 'week', label: 'Semanal' },
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'month', label: 'Mensual' },
];

const periodTypeLabel = (type) => PERIOD_TYPES.find((p) => p.value === type)?.label || type;

/** Fecha calendario sin desfase UTC → zona local (ej. 16 abr no se muestra como 15 abr). */
const parseCalendarDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const formatCalendarDate = (value, pattern = 'd MMM yyyy') => {
  const d = parseCalendarDate(value);
  if (!d) return '—';
  return format(d, pattern, { locale: es });
};

const formatPeriodRange = (start, end) => {
  if (!start || !end) return '—';
  return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`;
};

const computePeriodEnd = (periodType, periodStart) => {
  const start = parseISO(periodStart);
  if (periodType === 'week') return format(addDays(start, 6), 'yyyy-MM-dd');
  if (periodType === 'biweekly') return format(addDays(start, 13), 'yyyy-MM-dd');
  if (periodType === 'month') return format(endOfMonth(start), 'yyyy-MM-dd');
  return periodStart;
};

const defaultRunForm = () => {
  const now = new Date();
  const periodStart = format(startOfMonth(now), 'yyyy-MM-dd');
  return {
    periodType: 'month',
    periodStart,
    periodEnd: format(endOfMonth(now), 'yyyy-MM-dd'),
    title: '',
    notes: '',
    prefillFromSalaries: true,
    branchId: '',
  };
};

const defaultPaymentForm = () => ({
  employeeId: '',
  grossAmount: '',
  discountAmount: '0',
  discountReason: '',
  notes: '',
});

const Payroll = () => {
  const { isAdmin, loading: authLoading, user } = useAuth();
  const [mainTab, setMainTab] = useState(0);
  const [tab, setTab] = useState(0);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [runFilterType, setRunFilterType] = useState('all');
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [runForm, setRunForm] = useState(defaultRunForm);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState(defaultPaymentForm);
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  const [expenses, setExpenses] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensePeriodType, setExpensePeriodType] = useState('all');
  const [expenseFrom, setExpenseFrom] = useState(() =>
    format(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [expenseTo, setExpenseTo] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [expenseBranch, setExpenseBranch] = useState('all');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsedFallback(false);
    try {
      const params = { status: statusFilter };
      if (branchFilter !== 'all') params.branchId = branchFilter;
      const { data } = await getPayrollSummary(params);
      setSummary(data);
    } catch (err) {
      console.error('Error al cargar nómina:', err);
      const status = err.response?.status;
      if (status === 404 || status === 502 || status === 503) {
        try {
          const { data: employees } = await getEmployees(
            statusFilter !== 'all' ? { status: statusFilter } : {}
          );
          setSummary(buildSummaryFromEmployees(employees, branchFilter, statusFilter));
          setUsedFallback(true);
          setError(
            'Se usó un cálculo local (lista de empleados). Reinicia el backend con npm run dev para activar la API de nómina.'
          );
          return;
        } catch (fallbackErr) {
          console.error(fallbackErr);
        }
      }
      setSummary(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [branchFilter, statusFilter]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const params = {};
      if (runFilterType !== 'all') params.periodType = runFilterType;
      if (branchFilter !== 'all') params.branchId = branchFilter;
      const { data } = await getPayrollRuns(params);
      setRuns(data);
    } catch (err) {
      console.error(err);
    } finally {
      setRunsLoading(false);
    }
  }, [runFilterType, branchFilter]);

  const loadRunDetail = useCallback(async (id) => {
    if (!id) {
      setRunDetail(null);
      return;
    }
    try {
      const { data } = await getPayrollRun(id);
      setRunDetail(data);
    } catch (err) {
      console.error(err);
      setRunDetail(null);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setExpensesLoading(true);
    try {
      const params = {
        periodType: expensePeriodType,
        from: expenseFrom,
        to: expenseTo,
      };
      if (expenseBranch !== 'all') params.branchId = expenseBranch;
      const { data } = await getPayrollExpenses(params);
      setExpenses(data);
    } catch (err) {
      console.error(err);
      setExpenses(null);
    } finally {
      setExpensesLoading(false);
    }
  }, [expensePeriodType, expenseFrom, expenseTo, expenseBranch]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const { data } = await getBranches();
        setBranches(data);
      } catch (error) {
        console.error(error);
      }
    };
    const loadEmps = async () => {
      try {
        const { data } = await getEmployees({ status: 'active' });
        setEmployees(data);
      } catch (error) {
        console.error(error);
      }
    };
    loadBranches();
    loadEmps();
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) {
      loadSummary();
    } else {
      setLoading(false);
    }
  }, [authLoading, isAdmin, loadSummary]);

  useEffect(() => {
    if (!isAdmin || mainTab !== 1) return;
    loadRuns();
  }, [isAdmin, mainTab, loadRuns]);

  useEffect(() => {
    if (selectedRunId) loadRunDetail(selectedRunId);
  }, [selectedRunId, loadRunDetail]);

  useEffect(() => {
    if (!isAdmin || mainTab !== 2) return;
    loadExpenses();
  }, [isAdmin, mainTab, loadExpenses]);

  const handleCreateRun = async () => {
    try {
      const payload = {
        periodType: runForm.periodType,
        periodStart: runForm.periodStart,
        periodEnd: runForm.periodEnd,
        title: runForm.title,
        notes: runForm.notes,
        prefillFromSalaries: runForm.prefillFromSalaries,
      };
      if (runForm.branchId) payload.branchId = runForm.branchId;
      const { data } = await createPayrollRun(payload);
      setOpenRunDialog(false);
      setRunForm(defaultRunForm());
      await loadRuns();
      setSelectedRunId(data._id);
    } catch (err) {
      alert(err.response?.data?.message || 'No se pudo crear el corte');
    }
  };

  const handleToggleRunStatus = async () => {
    if (!runDetail) return;
    const next = runDetail.status === 'closed' ? 'open' : 'closed';
    try {
      const { data } = await updatePayrollRun(runDetail._id, { status: next });
      setRunDetail(data);
      loadRuns();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al actualizar el corte');
    }
  };

  const handleDeleteRun = async () => {
    if (!runDetail || !window.confirm('¿Eliminar este corte y todos sus pagos registrados?')) return;
    try {
      await deletePayrollRun(runDetail._id);
      setSelectedRunId(null);
      setRunDetail(null);
      loadRuns();
    } catch (err) {
      alert(err.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const handleSavePayment = async () => {
    if (!runDetail) return;
    try {
      await upsertPayrollPayment(runDetail._id, {
        employeeId: paymentForm.employeeId,
        grossAmount: Number(paymentForm.grossAmount),
        discountAmount: Number(paymentForm.discountAmount) || 0,
        discountReason: paymentForm.discountReason,
        notes: paymentForm.notes,
      });
      setOpenPaymentDialog(false);
      setPaymentForm(defaultPaymentForm());
      setEditingPaymentId(null);
      loadRunDetail(runDetail._id);
      loadRuns();
    } catch (err) {
      alert(err.response?.data?.message || 'No se pudo guardar el pago');
    }
  };

  const openEditPayment = (payment) => {
    setEditingPaymentId(payment._id);
    setPaymentForm({
      employeeId: payment.employee?._id || payment.employee,
      grossAmount: String(payment.grossAmount ?? ''),
      discountAmount: String(payment.discountAmount ?? 0),
      discountReason: payment.discountReason || '',
      notes: payment.notes || '',
    });
    setOpenPaymentDialog(true);
  };

  const openNewPayment = () => {
    setEditingPaymentId(null);
    setPaymentForm(defaultPaymentForm());
    setOpenPaymentDialog(true);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!runDetail || !window.confirm('¿Quitar este pago del corte?')) return;
    try {
      await deletePayrollPayment(runDetail._id, paymentId);
      loadRunDetail(runDetail._id);
      loadRuns();
    } catch (err) {
      alert(err.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const handleExportRun = async () => {
    if (!runDetail) return;
    try {
      const res = await exportPayrollRunCsv(runDetail._id);
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `corte_${formatCalendarDate(runDetail.periodStart, 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('No se pudo exportar el corte');
    }
  };

  const handleRunFormPeriodType = (periodType) => {
    const periodEnd = computePeriodEnd(periodType, runForm.periodStart);
    setRunForm((f) => ({ ...f, periodType, periodEnd }));
  };

  const handleRunFormStart = (periodStart) => {
    const periodEnd = computePeriodEnd(runForm.periodType, periodStart);
    setRunForm((f) => ({ ...f, periodStart, periodEnd }));
  };

  const netPreview =
    (Number(paymentForm.grossAmount) || 0) - (Number(paymentForm.discountAmount) || 0);

  const handleExport = async () => {
    try {
      const params = { status: statusFilter };
      if (branchFilter !== 'all') params.branchId = branchFilter;
      const res = await exportPayrollCsv(params);
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nomina_${format(new Date(), 'yyyy-MM')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('No se pudo exportar la nómina');
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="lg">
        <Typography sx={{ py: 4 }}>Verificando sesión…</Typography>
      </Container>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const periodLabel = format(new Date(), "MMMM yyyy", { locale: es });

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentsIcon color="primary" />
            Pago de nómina
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Referencia: {periodLabel} · registra cortes semanales, quincenales o mensuales con descuentos y reporte de
            gastos
          </Typography>
        </Box>
        {mainTab === 0 && (
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={loading}>
            Exportar salarios CSV
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Salarios de referencia" />
          <Tab label="Cortes de pago" />
          <Tab label="Reporte de gastos" />
        </Tabs>
      </Paper>

      {mainTab === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Los importes de referencia usan el campo <strong>salario</strong> de cada empleado. En{' '}
          <strong>Cortes de pago</strong> registras lo realmente pagado (bruto, descuentos y neto) por semana, quincena o
          mes.
        </Alert>
      )}

      {mainTab === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Crea un corte por período, registra el pago de cada empleado y los descuentos aplicados. Al cerrar el corte ya
          no se editan los pagos (puedes reabrirlo si necesitas corregir).
        </Alert>
      )}

      {usedFallback && error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {error && !summary && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadSummary}>
              Reintentar
            </Button>
          }
        >
          {error}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Sesión: {user?.email} · rol: {user?.role || '—'}. La nómina requiere rol <strong>admin</strong>.
          </Typography>
        </Alert>
      )}

      {(mainTab === 0 || mainTab === 1) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sucursal</InputLabel>
                <Select
                  label="Sucursal"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas las sucursales</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b._id} value={b._id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {mainTab === 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado del empleado</InputLabel>
                  <Select
                    label="Estado del empleado"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="active">Solo activos</MenuItem>
                    <MenuItem value="on_leave">De permiso</MenuItem>
                    <MenuItem value="inactive">Inactivos</MenuItem>
                    <MenuItem value="all">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {mainTab === 1 && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de corte</InputLabel>
                  <Select
                    label="Tipo de corte"
                    value={runFilterType}
                    onChange={(e) => setRunFilterType(e.target.value)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {PERIOD_TYPES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {mainTab === 0 && summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Total a pagar
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {formatMoney(summary.totals.grandTotal)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Empleados en el reporte
                </Typography>
                <Typography variant="h5">{summary.totals.employeeCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Con salario definido
                </Typography>
                <Typography variant="h5">{summary.totals.withSalaryCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Sin salario (revisar)
                </Typography>
                <Typography variant="h5" color={summary.totals.withoutSalaryCount ? 'warning.main' : 'inherit'}>
                  {summary.totals.withoutSalaryCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {mainTab === 0 && (
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Por sucursal" />
            <Tab label="Por empleado" />
          </Tabs>
        </Paper>
      )}

      {mainTab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Cortes</Typography>
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={loadRuns} aria-label="Actualizar">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setRunForm(defaultRunForm());
                      setOpenRunDialog(true);
                    }}
                  >
                    Nuevo
                  </Button>
                </Stack>
              </Stack>
              {runsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando…
                </Typography>
              ) : runs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay cortes. Crea el primero con «Nuevo».
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {runs.map((run) => (
                    <Card
                      key={run._id}
                      variant={selectedRunId === run._id ? 'outlined' : 'elevation'}
                      sx={{
                        cursor: 'pointer',
                        borderColor: selectedRunId === run._id ? 'primary.main' : undefined,
                        borderWidth: selectedRunId === run._id ? 2 : 0,
                      }}
                      onClick={() => setSelectedRunId(run._id)}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {run.title || periodTypeLabel(run.periodType)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatPeriodRange(run.periodStart, run.periodEnd)}
                            </Typography>
                            <Chip
                              size="small"
                              label={periodTypeLabel(run.periodType)}
                              sx={{ mt: 0.5, mr: 0.5 }}
                            />
                            <Chip
                              size="small"
                              color={run.status === 'closed' ? 'default' : 'success'}
                              label={run.status === 'closed' ? 'Cerrado' : 'Abierto'}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="subtitle2" color="primary.main">
                            {formatMoney(run.totals?.totalNet)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {run.totals?.paymentCount || 0} pagos · desc.{' '}
                          {formatMoney(run.totals?.totalDiscount)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {!runDetail ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Selecciona un corte de la lista o crea uno nuevo.
                </Typography>
              </Paper>
            ) : (
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6">
                      {runDetail.title || periodTypeLabel(runDetail.periodType)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPeriodRange(runDetail.periodStart, runDetail.periodEnd)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Tooltip title={runDetail.status === 'closed' ? 'Reabrir corte' : 'Cerrar corte'}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={runDetail.status === 'closed' ? <LockOpenIcon /> : <LockIcon />}
                        onClick={handleToggleRunStatus}
                      >
                        {runDetail.status === 'closed' ? 'Reabrir' : 'Cerrar'}
                      </Button>
                    </Tooltip>
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportRun}>
                      CSV
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openNewPayment}
                      disabled={runDetail.status === 'closed'}
                    >
                      Agregar pago
                    </Button>
                    <IconButton size="small" color="error" onClick={handleDeleteRun} aria-label="Eliminar corte">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Bruto
                        </Typography>
                        <Typography variant="h6">{formatMoney(runDetail.totals?.totalGross)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Descuentos
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                          {formatMoney(runDetail.totals?.totalDiscount)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Neto pagado
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          {formatMoney(runDetail.totals?.totalNet)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Empleados
                        </Typography>
                        <Typography variant="h6">{runDetail.totals?.paymentCount || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Empleado</TableCell>
                        <TableCell align="right">Bruto</TableCell>
                        <TableCell align="right">Descuento</TableCell>
                        <TableCell>Motivo</TableCell>
                        <TableCell align="right">Neto</TableCell>
                        <TableCell width={80} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(runDetail.payments || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary">
                              Sin pagos. Usa «Agregar pago» o crea el corte con precarga desde salarios.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        runDetail.payments.map((p) => (
                          <TableRow
                            key={p._id}
                            hover
                            sx={{ cursor: runDetail.status === 'open' ? 'pointer' : 'default' }}
                            onClick={() => runDetail.status === 'open' && openEditPayment(p)}
                          >
                            <TableCell>
                              {p.employee?.firstName} {p.employee?.lastName}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {p.employee?.branch?.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatMoney(p.grossAmount)}</TableCell>
                            <TableCell align="right">{formatMoney(p.discountAmount)}</TableCell>
                            <TableCell>{p.discountReason || '—'}</TableCell>
                            <TableCell align="right">
                              <strong>{formatMoney(p.netAmount)}</strong>
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              {runDetail.status === 'open' && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeletePayment(p._id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {mainTab === 2 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de corte</InputLabel>
                  <Select
                    label="Tipo de corte"
                    value={expensePeriodType}
                    onChange={(e) => setExpensePeriodType(e.target.value)}
                  >
                    <MenuItem value="all">Todos los tipos</MenuItem>
                    {PERIOD_TYPES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Desde"
                  type="date"
                  value={expenseFrom}
                  onChange={(e) => setExpenseFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hasta"
                  type="date"
                  value={expenseTo}
                  onChange={(e) => setExpenseTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sucursal</InputLabel>
                  <Select
                    label="Sucursal"
                    value={expenseBranch}
                    onChange={(e) => setExpenseBranch(e.target.value)}
                  >
                    <MenuItem value="all">Todas</MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadExpenses}
                  disabled={expensesLoading}
                >
                  Actualizar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {expensesLoading ? (
            <Typography color="text.secondary">Cargando reporte…</Typography>
          ) : expenses ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">
                        Total neto pagado
                      </Typography>
                      <Typography variant="h5" color="primary.main">
                        {formatMoney(expenses.totals?.totalNet)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">
                        Descuentos en el período
                      </Typography>
                      <Typography variant="h5" color="warning.main">
                        {formatMoney(expenses.totals?.totalDiscount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">
                        Cortes registrados
                      </Typography>
                      <Typography variant="h5">{expenses.totals?.runCount || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {expenses.byPeriodType?.length > 0 && (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo de período</TableCell>
                        <TableCell align="right">Cortes</TableCell>
                        <TableCell align="right">Bruto</TableCell>
                        <TableCell align="right">Descuentos</TableCell>
                        <TableCell align="right">Neto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenses.byPeriodType.map((row) => (
                        <TableRow key={row.periodType}>
                          <TableCell>{row.periodTypeLabel}</TableCell>
                          <TableCell align="right">{row.runCount}</TableCell>
                          <TableCell align="right">{formatMoney(row.totalGross)}</TableCell>
                          <TableCell align="right">{formatMoney(row.totalDiscount)}</TableCell>
                          <TableCell align="right">
                            <strong>{formatMoney(row.totalNet)}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Corte / período</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Pagos</TableCell>
                      <TableCell align="right">Bruto</TableCell>
                      <TableCell align="right">Descuentos</TableCell>
                      <TableCell align="right">Neto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenses.periods?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No hay cortes en el rango seleccionado. Registra pagos en «Cortes de pago».
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.periods.map((row) => (
                        <TableRow key={row.runId} hover>
                          <TableCell>
                            <Typography fontWeight="medium">
                              {row.title || formatPeriodRange(row.periodStart, row.periodEnd)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatPeriodRange(row.periodStart, row.periodEnd)}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.periodTypeLabel}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.status === 'closed' ? 'Cerrado' : 'Abierto'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{row.paymentCount}</TableCell>
                          <TableCell align="right">{formatMoney(row.totalGross)}</TableCell>
                          <TableCell align="right">{formatMoney(row.totalDiscount)}</TableCell>
                          <TableCell align="right">
                            <strong>{formatMoney(row.totalNet)}</strong>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {expenses.periods?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatMoney(expenses.totals?.totalGross)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatMoney(expenses.totals?.totalDiscount)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary.main">
                            {formatMoney(expenses.totals?.totalNet)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography color="text.secondary">No se pudo cargar el reporte.</Typography>
          )}
        </>
      )}

      <Dialog open={openRunDialog} onClose={() => setOpenRunDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo corte de nómina</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de período</InputLabel>
              <Select
                label="Tipo de período"
                value={runForm.periodType}
                onChange={(e) => handleRunFormPeriodType(e.target.value)}
              >
                {PERIOD_TYPES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fecha inicio"
              type="date"
              size="small"
              fullWidth
              value={runForm.periodStart}
              onChange={(e) => handleRunFormStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Fecha fin"
              type="date"
              size="small"
              fullWidth
              value={runForm.periodEnd}
              onChange={(e) => setRunForm((f) => ({ ...f, periodEnd: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Título (opcional)"
              size="small"
              fullWidth
              placeholder="Ej. Quincena 1 - Mayo 2026"
              value={runForm.title}
              onChange={(e) => setRunForm((f) => ({ ...f, title: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Sucursal (precarga)</InputLabel>
              <Select
                label="Sucursal (precarga)"
                value={runForm.branchId}
                onChange={(e) => setRunForm((f) => ({ ...f, branchId: e.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={runForm.prefillFromSalaries}
                  onChange={(e) => setRunForm((f) => ({ ...f, prefillFromSalaries: e.target.checked }))}
                />
              }
              label="Precargar montos desde salario (mensual ÷ 2 quincena, ÷ 4 semana)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRunDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateRun}>
            Crear corte
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPaymentId ? 'Editar pago' : 'Registrar pago'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" disabled={!!editingPaymentId}>
              <InputLabel>Empleado</InputLabel>
              <Select
                label="Empleado"
                value={paymentForm.employeeId}
                onChange={(e) => {
                  const emp = employees.find((x) => x._id === e.target.value);
                  const suggested =
                    runDetail && emp?.salary != null
                      ? runDetail.periodType === 'month'
                        ? emp.salary
                        : runDetail.periodType === 'biweekly'
                          ? Math.round((emp.salary / 2) * 100) / 100
                          : Math.round((emp.salary / 4) * 100) / 100
                      : '';
                  setPaymentForm((f) => ({
                    ...f,
                    employeeId: e.target.value,
                    grossAmount: f.grossAmount || (suggested !== '' ? String(suggested) : ''),
                  }));
                }}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    {emp.firstName} {emp.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Bruto a pagar"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              value={paymentForm.grossAmount}
              onChange={(e) => setPaymentForm((f) => ({ ...f, grossAmount: e.target.value }))}
            />
            <TextField
              label="Descuento"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              value={paymentForm.discountAmount}
              onChange={(e) => setPaymentForm((f) => ({ ...f, discountAmount: e.target.value }))}
            />
            <TextField
              label="Motivo del descuento"
              size="small"
              fullWidth
              value={paymentForm.discountReason}
              onChange={(e) => setPaymentForm((f) => ({ ...f, discountReason: e.target.value }))}
              placeholder="Ej. préstamo, faltante, IMSS…"
            />
            <Alert severity="info">
              Neto a pagar: <strong>{formatMoney(Math.max(0, netPreview))}</strong>
            </Alert>
            <TextField
              label="Notas (opcional)"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSavePayment}
            disabled={!paymentForm.employeeId || paymentForm.grossAmount === ''}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {mainTab === 0 && (loading ? (
        <Typography color="text.secondary">Cargando nómina…</Typography>
      ) : !summary ? (
        <Box>
          <Typography color="text.secondary" gutterBottom>
            No se pudo cargar el resumen.
          </Typography>
          <Button variant="contained" onClick={loadSummary} sx={{ mt: 1 }}>
            Reintentar
          </Button>
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sucursal</TableCell>
                    <TableCell align="right">Empleados</TableCell>
                    <TableCell align="right">Con salario</TableCell>
                    <TableCell align="right">Sin salario</TableCell>
                    <TableCell align="right">Subtotal a pagar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byBranch.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No hay empleados con los filtros seleccionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.byBranch.map((row) => (
                      <TableRow key={row.branch?._id || 'none'} hover>
                        <TableCell>
                          <Typography fontWeight="medium">{row.branch?.name || 'Sin sucursal'}</Typography>
                        </TableCell>
                        <TableCell align="right">{row.employeeCount}</TableCell>
                        <TableCell align="right">{row.withSalaryCount}</TableCell>
                        <TableCell align="right">{row.withoutSalaryCount}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">{formatMoney(row.subtotal)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {summary.byBranch.length > 0 && (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Total general</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{summary.totals.employeeCount}</strong>
                      </TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary.main">
                          {formatMoney(summary.totals.grandTotal)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Empleado</TableCell>
                    <TableCell>Nº emp.</TableCell>
                    <TableCell>Puesto</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Salario mensual</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No hay empleados con los filtros seleccionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.employees.map((emp) => (
                      <TableRow key={emp._id} hover>
                        <TableCell>
                          {emp.firstName} {emp.lastName}
                        </TableCell>
                        <TableCell>{emp.employeeNumber || '—'}</TableCell>
                        <TableCell>{emp.position}</TableCell>
                        <TableCell>{emp.branch?.name || '—'}</TableCell>
                        <TableCell>
                          <Chip label={statusLabel(emp.status)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          {emp.salary != null ? (
                            <Typography fontWeight="medium">{formatMoney(emp.salary)}</Typography>
                          ) : (
                            <Chip label="Sin salario" size="small" color="warning" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ))}
    </Container>
  );
};

export default Payroll;
